import type { PlanetName } from './astroEngine';
import { classifyFunctionalForLagna } from './astroEngine';
import { getHouseLord } from './lordshipUtils';
export type AspectEdge = {
  from: PlanetName;
  toHouse: number; // 1..12
};

type PlanetStrengthMap = Required<Record<PlanetName, number>>;

export function computeHouseScoresWithAspects(
  planetPct: PlanetStrengthMap,
  placementsD1: Partial<Record<PlanetName, { house: number }>>,
  aspects: AspectEdge[],
  opts?: {
    occupyW?: number;
    aspectWhenEmptyW?: number;
    aspectWhenOccupiedW?: number;
    clampTo?: number;
  }
): number[] {
  const occupyW = opts?.occupyW ?? 1.0;
  const aspectWEmpty = opts?.aspectWhenEmptyW ?? 1.0;
  const aspectWOccupied = opts?.aspectWhenOccupiedW ?? 0.5;
  const clampTo = typeof opts?.clampTo === 'number' && opts.clampTo > 0 ? opts.clampTo : null;

  const occupants: Record<number, PlanetName[]> = Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => [i + 1, [] as PlanetName[]])
  ) as Record<number, PlanetName[]>;

  (Object.keys(placementsD1) as PlanetName[]).forEach((planet) => {
    const house = placementsD1[planet]?.house;
    if (typeof house === 'number' && house >= 1 && house <= 12) {
      occupants[house].push(planet);
    }
  });

  const houseScore = Array.from({ length: 12 }, (_, idx) => {
    const house = idx + 1;
    const occ = occupants[house];
    if (!occ || occ.length === 0) return 0;
    return occ.reduce((sum, planet) => sum + (planetPct[planet] ?? 0) * occupyW, 0);
  });

  const aspectors: Record<number, PlanetName[]> = Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => [i + 1, [] as PlanetName[]])
  ) as Record<number, PlanetName[]>;

  aspects.forEach(({ from, toHouse }) => {
    if (toHouse >= 1 && toHouse <= 12) {
      aspectors[toHouse].push(from);
    }
  });

  for (let house = 1; house <= 12; house++) {
    const isEmpty = occupants[house].length === 0;
    const weight = isEmpty ? aspectWEmpty : aspectWOccupied;
    for (const planet of aspectors[house]) {
      houseScore[house - 1] += (planetPct[planet] ?? 0) * weight;
    }
  }

  if (clampTo) {
    for (let i = 0; i < 12; i++) {
      houseScore[i] = Math.max(-clampTo, Math.min(clampTo, houseScore[i]));
    }
  }

  return houseScore;
}
// utils/houseScores.ts  (add below existing exports)
//import type { PlanetName } from './astroEngine';

export type FuncRole = 'benefic'|'malefic'|'neutral';

/** Advanced house scoring per your spec. */
export function computeHouseScoresAdvanced(
  planetBlendedPct: Required<Record<PlanetName, number>>,      // from Step 1
  placementsD1: Record<PlanetName, { house: number }>,         // D1 houses (1..12)
  aspects: Array<{ from: PlanetName; toHouse: number }>,       // Parāśari (house-mode)
  signLordOf: (sign1to12: number) => PlanetName,               // resolver for house lords
  lagnaSign1to12: number,                                      // D1 Lagna sign
  funcRole: (p: PlanetName) => FuncRole,                       // functional map by Lagna
  opts?: {
    // occupancy weights by functional role
    occupyW?: { benefic:number; malefic:number; neutral:number };
    // aspect weights (empty / occupied)
    aspectW?: { empty:number; occupied:number };
    // lord weights (empty+noAspect / empty+hasAspect / occupied)
    lordW?: { emptyNoAspect:number; emptyHasAspect:number; occupied:number };
    clampTo?: number | null;  // optional safety clamp
  }
): number[] {
  const O = {
    occupyW: { benefic: +1.0, malefic: -1.0, neutral: +1.0, ...(opts?.occupyW||{}) },
    aspectW: { empty: 1.0, occupied: 0.5, ...(opts?.aspectW||{}) },
    lordW:   { emptyNoAspect:1.0, emptyHasAspect:0.75, occupied:0.5, ...(opts?.lordW||{}) },
    clampTo: (typeof opts?.clampTo==='number' ? opts?.clampTo : null)
  };

  // 1) Occupants & aspectors per house
  const occupants: Record<number, PlanetName[]> = Object.fromEntries([...Array(12)].map((_,i)=>[i+1,[]])) as any;
  for (const p of Object.keys(placementsD1) as PlanetName[]) {
    const h = placementsD1[p]?.house|0;
    if (h>=1 && h<=12) occupants[h].push(p);
  }
  const aspectors: Record<number, PlanetName[]> = Object.fromEntries([...Array(12)].map((_,i)=>[i+1,[]])) as any;
  for (const e of aspects) { if (e.toHouse>=1 && e.toHouse<=12) aspectors[e.toHouse].push(e.from); }

  // 2) House lord for each house (whole sign from Lagna)
  const houseSign = (h:number) => ((lagnaSign1to12 - 1 + (h - 1)) % 12) + 1; // anti-clockwise
  const houseLord: Record<number, PlanetName> = {} as any;
  for (let h=1; h<=12; h++) houseLord[h] = signLordOf(houseSign(h));

  // 3) Build scores
  const score = Array(12).fill(0) as number[];

  for (let h=1; h<=12; h++) {
    const occ = occupants[h];
    const hasOcc = occ.length > 0;
    const asps = aspectors[h];
    const hasAsp = asps.length > 0;

    // 3a) House-lord bonus
    const lord = houseLord[h];
    const lordPct = planetBlendedPct[lord] ?? 0;
    if (!hasOcc && !hasAsp) score[h-1] += O.lordW.emptyNoAspect * lordPct;
    else if (!hasOcc && hasAsp) score[h-1] += O.lordW.emptyHasAspect * lordPct;
    else score[h-1] += O.lordW.occupied * lordPct;

    // 3b) Occupants (signed by FB/FM)
    for (const p of occ) {
      const role = funcRole(p);
      const w = O.occupyW[role];
      score[h-1] += w * (planetBlendedPct[p] ?? 0);
    }

    // 3c) Aspects (signed by FB/FM; empty/occupied weight)
    const aspW = hasOcc ? O.aspectW.occupied : O.aspectW.empty;
    for (const p of asps) {
      const role = funcRole(p);
      const signed = (role === 'malefic') ? -1 : +1; // neutral treated as + by default
      score[h-1] += aspW * signed * (planetBlendedPct[p] ?? 0);
    }

    // optional clamp
    if (O.clampTo != null) score[h-1] = Math.max(-O.clampTo, Math.min(O.clampTo, score[h-1]));
  }

  return score;
}
// ADD below the existing exports in houseScores.ts

/**
 * New: lords + functional benefic/malefic aware scoring.
 * - House-lord weight: 1.0 if empty & no aspect, 0.75 if empty & has aspects, 0.5 if occupied.
 * - Occupants: add if functional benefic, subtract if functional malefic (weight 1.0).
 * - Aspectors: + if benefic, − if malefic; weight 1.0 when empty, 0.5 when occupied.
 * - All inputs are signed planet strengths in [-100..100].
 */
export function computeHouseScoresWithLordsAndAspects(
  planetPct: Required<Record<PlanetName, number>>,
  placementsD1: Record<PlanetName, { house: number }>,
  aspects: AspectEdge[],
  ascSign1to12: number,
  opts?: { clampTo?: number }
): number[] {
  const clampTo = typeof opts?.clampTo === 'number' && opts.clampTo > 0 ? opts.clampTo : null;

  // Occupants by house
  const occupants: Record<number, PlanetName[]> = Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => [i + 1, [] as PlanetName[]])
  ) as Record<number, PlanetName[]>;
  (Object.keys(placementsD1) as PlanetName[]).forEach((p) => {
    const h = placementsD1[p]?.house;
    if (h >= 1 && h <= 12) occupants[h].push(p);
  });

  // Aspectors by house
  const aspectors: Record<number, PlanetName[]> = Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => [i + 1, [] as PlanetName[]])
  ) as Record<number, PlanetName[]>;
  aspects.forEach(({ from, toHouse }) => {
    if (toHouse >= 1 && toHouse <= 12) aspectors[toHouse].push(from);
  });

  // Functional map for current lagna (7 grahas; treat Rahu/Ketu as malefic)
  const func = classifyFunctionalForLagna(ascSign1to12) as Record<string,'benefic'|'malefic'|'neutral'>;

  const role = (p: PlanetName): 'benefic'|'malefic'|'neutral' =>
    (p === 'Rahu' || p === 'Ketu') ? 'malefic' : (func[p] ?? 'neutral');

  const score: number[] = Array(12).fill(0);

  for (let house = 1; house <= 12; house++) {
    const occ = occupants[house];
    const hasOcc = occ.length > 0;
    const hasAsp = aspectors[house].length > 0;

    // 1) House lord contribution with 1.0 / 0.75 / 0.5
    const lord = getHouseLord(house as any, ascSign1to12);
    const lordPct = planetPct[lord] ?? 0;
    const lordW = hasOcc ? 0.5 : (hasAsp ? 0.75 : 1.0);
    score[house - 1] += lordPct * lordW;

    // 2) Occupying planets (benefic add, malefic subtract), weight 1.0
    for (const p of occ) {
      const val = planetPct[p] ?? 0;
      const sgn = role(p) === 'malefic' ? -1 : (role(p) === 'benefic' ? +1 : 0);
      score[house - 1] += val * sgn * 1.0;
    }

    // 3) Aspecting planets (benefic add, malefic subtract), 1.0 if empty, 0.5 if occupied
    const aspW = hasOcc ? 0.5 : 1.0;
    for (const p of aspectors[house]) {
      const val = planetPct[p] ?? 0;
      const sgn = role(p) === 'malefic' ? -1 : (role(p) === 'benefic' ? +1 : 0);
      score[house - 1] += val * sgn * aspW;
    }

    if (clampTo) {
      score[house - 1] = Math.max(-clampTo, Math.min(clampTo, score[house - 1]));
    }
  }

  return score;
}
